import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Send,
  Loader2,
  Brain,
  User,
  Database,
  Sparkles,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  Download,
  FileDown,
  FileText,
  FileJson
} from 'lucide-react';
import { BigDataDataset } from '../../services/BigDataDatasetService';
import BigDataExpertService, { ExpertSession } from '../../services/BigDataExpertService';
import BigDataInsightsService from '../../services/BigDataInsightsService';
import { useAuth } from '../auth/AuthProvider';
import { useBigDataChatRealtime } from '../../hooks/useBigDataChatRealtime';
import { useToast } from '../ui/Toast';
import { supabase } from '../../lib/supabase';
import { debugLog } from '../../utils/debug-logger';
import logger from '../../utils/logger';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  query?: string; // SQL query executed
  resultCount?: number;
  confidence?: number;
  executionTime?: number;
  insights?: any[];
  exportFile?: {
    filename: string;
    url: string;
    rowCount: number;
    size: number;
  };
}

interface DatasetChatProps {
  dataset: BigDataDataset;
}

const DatasetChat: React.FC<DatasetChatProps> = ({ 
  dataset
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expertSession, setExpertSession] = useState<ExpertSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize expert session for this dataset
    initializeExpertSession();
  }, [dataset.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ✅ FONCTION EXPORT - Générer export au format demandé
  const handleExport = async (message: Message, format: 'csv' | 'json' | 'markdown' | 'pdf') => {
    // ✅ FIX: Utiliser exportFile si disponible, sinon regénérer
    if (!expertSession) {
      toast.error('Session expirée. Veuillez recharger la page.');
      return;
    }
    
    setExportingFormat(format);
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api-clairefy.darknessbind.com';
      
      // ✅ Si exportFile existe déjà, télécharger direct
      if (format === 'csv' && message.exportFile?.url) {
        console.log('[Export] Using existing exportFile:', message.exportFile);
        toast.info(`Téléchargement de ${message.exportFile.rowCount.toLocaleString()} lignes...`);
        const downloadUrl = `${apiUrl}${message.exportFile.url}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = message.exportFile.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Export CSV réussi ! (${message.exportFile.rowCount.toLocaleString()} lignes)`);
        setExportingFormat(null);
        return;
      }
      
      if (format === 'pdf') {
        // Utiliser downloadPDF comme FluidChat
        toast.info('Génération du PDF en cours...');
        const { downloadPDF } = await import('../../utils/pdfExportV3');
        await downloadPDF(message.content, {
          title: 'Analyse Big Data',
          subtitle: dataset.file_name,
          author: 'Clairify AI',
          timestamp: true
        }, `bigdata_${Date.now()}.pdf`);
        toast.success('Export PDF réussi !');
        setExportingFormat(null);
        return;
      }
      
      // ✅ EXPORT JSON - Côté client
      if (format === 'json') {
        console.log('[Export] Generating JSON...');
        const jsonData = {
          dataset: dataset.file_name,
          query: message.content.split('\n')[0], // Première ligne = description
          answer: message.content,
          insights: message.insights || [],
          resultCount: message.resultCount || 0,
          timestamp: new Date(message.timestamp).toISOString()
        };
        
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bigdata_${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Export JSON réussi !');
        setExportingFormat(null);
        return;
      }
      
      // ✅ EXPORT MARKDOWN - Côté client
      if (format === 'markdown') {
        console.log('[Export] Generating Markdown...');
        const mdContent = `# Analyse Big Data\n\n**Dataset:** ${dataset.file_name}\n**Date:** ${new Date().toLocaleDateString()}\n\n## Résultat\n\n${message.content}\n\n## Insights\n\n${message.insights?.map((i: any) => `- **${i.title || i.type}**: ${i.findings || i.message}`).join('\n') || 'Aucun insight'}\n`;
        
        const blob = new Blob([mdContent], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bigdata_${Date.now()}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Export Markdown réussi !');
        setExportingFormat(null);
        return;
      }
      
      // ✅ EXPORT CSV - Uniquement si exportFile existe
      if (format === 'csv') {
        console.log('[Export] CSV requested but no exportFile. Informing user...');
        toast.warning('Fichier CSV complet non disponible pour ce message.\n\nPour obtenir un export CSV avec toutes les lignes, relancez votre requête.');
        setExportingFormat(null);
        return;
      }
      
    } catch (error) {
      console.error('[Export] Error:', error);
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}: ${error instanceof Error ? error.message : 'Unknown'}`);
    } finally {
      setExportingFormat(null);
    }
  };

  // ✅ REALTIME SUPABASE - Sync automatique des insights et queries
  const { isConnected, insightCount, queryCount } = useBigDataChatRealtime({
    sessionId: expertSession?.supabaseSessionId || '',
    userId: user?.id || '',
    datasetId: dataset.id,
    enabled: !!expertSession && !!user,
    onInsight: (insight) => {
      // Notifier l'utilisateur
      setMessages(prev => [...prev, {
        id: `insight-${insight.id}`,
        role: 'system',
        content: `💡 Nouvelle découverte: ${insight.title}\n\n${insight.description}`,
        timestamp: Date.now()
      }]);
    },
    onQuery: (_query) => {
      // Query logged silently
    }
  });

  // REMOVED: Excessive realtime connection logs

  const initializeExpertSession = async () => {
    if (!user) return;
    
    debugLog('[💬 DatasetChat] Initializing expert session...');
    debugLog('[📊 DatasetChat] Dataset status:', dataset.status);
    setIsInitializing(true);
    setError(null);

    try {
      // ✅ Vérifier que le dataset est complété
      if (dataset.status !== 'completed') {
        logger.warn('[⚠️ DatasetChat] Dataset not ready yet, status:', dataset.status);
        setMessages([{
          id: '1',
          role: 'system',
          content: `⏳ Dataset en cours de traitement...\n\nStatut: ${dataset.status}\n\nL'analyse IA sera disponible une fois le processing terminé. Veuillez patienter quelques instants et rafraîchir la page.`,
          timestamp: Date.now()
        }]);
        setIsInitializing(false);
        return;
      }

      // ✅ NO LOCALSTORAGE - Always get fresh session from backend
      debugLog('[🚀 DatasetChat] Initializing session from backend...');
      
      // Backend will check if session exists in Supabase, or create new one
      const session = await BigDataExpertService.initializeSession(dataset.job_id, user.id);
      
      setExpertSession(session);

      // ✅ RESTORE LOADING STATE - Check if there's an ongoing analysis
      const loadingKey = `analysis_loading_${dataset.id}`;
      const storedLoading = localStorage.getItem(loadingKey);
      if (storedLoading) {
        const { timestamp, query } = JSON.parse(storedLoading);
        // Si l'analyse date de moins de 5 minutes, on considère qu'elle est toujours en cours
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          debugLog('[🔄 DatasetChat] Restoring ongoing analysis state...');
          setIsLoading(true);
          // Ajouter un message de système pour indiquer qu'une analyse est en cours
          setMessages(prev => [...prev, {
            id: `restore-${Date.now()}`,
            role: 'system',
            content: `🔄 Analyse en cours : "${query}"\n\nL'analyse continue en arrière-plan...`,
            timestamp: Date.now()
          }]);
        } else {
          // Nettoyer si trop vieux
          localStorage.removeItem(loadingKey);
        }
      }

      // ✅ Charger l'historique des messages depuis Supabase
      debugLog('[📜 DatasetChat] Loading message history from Supabase...');
      debugLog('   Session UUID:', session.supabaseSessionId);
      
      const { data: historyQueries, error: historyError } = await supabase
        .from('big_data_queries')
        .select('*')
        .eq('session_id', session.supabaseSessionId)
        .order('created_at', { ascending: true })
        .limit(50);

      let loadedMessages: Message[] = [];

      if (!historyError && historyQueries && historyQueries.length > 0) {
        debugLog(`[✅ DatasetChat] Found ${historyQueries.length} previous queries`);
        
        // Convertir les queries en paires de messages (user + assistant)
        loadedMessages = historyQueries.flatMap((query: any) => {
          const msgs: Message[] = [];
          
          // Message utilisateur (question)
          msgs.push({
            id: `history-user-${query.id}`,
            role: 'user',
            content: query.question,
            timestamp: new Date(query.created_at).getTime()
          });
          
          // Message assistant (réponse)
          // ✅ UTILISER LA VRAIE RÉPONSE SAUVEGARDÉE
          let assistantContent = query.answer || ''; // ✅ FIX: Utiliser query.answer d'abord
          
          // Fallback sur insights si pas d'answer
          if (!assistantContent && query.insights && Array.isArray(query.insights) && query.insights.length > 0) {
            assistantContent = query.insights.map((insight: any) => 
              `${insight.title || insight.message || insight.description || ''}`
            ).join('\n\n');
          }
          
          if (!assistantContent && query.result_count) {
            assistantContent = `✅ Requête exécutée: ${query.result_count} résultats trouvés`;
          }
          
          if (assistantContent || query.sql_query) {
            msgs.push({
              id: `history-assistant-${query.id}`,
              role: 'assistant',
              content: assistantContent || 'Analyse effectuée',
              timestamp: new Date(query.created_at).getTime() + 1,
              query: query.sql_query,
              resultCount: query.result_count,
              executionTime: query.execution_time_ms,
              insights: query.insights
            });
          }
          
          return msgs;
        });
      } else if (historyError) {
        logger.error('[❌ DatasetChat] Error loading history:', historyError);
      } else {
        debugLog('[ℹ️ DatasetChat] No previous messages (new session)');
      }

      // ✅ Message de bienvenue avec fallback si summary manque
      const summary = session.summary || {
        rows: dataset.row_count || 0,
        columns: dataset.column_count || 0,
        patterns: 0,
        insights: 0
      };

      // Créer messages de bienvenue
      const welcomeMessages: Message[] = [];
      
      // Si historique existe, juste un petit message
      if (loadedMessages.length > 0) {
        welcomeMessages.push({
          id: 'welcome-back',
          role: 'system',
          content: `🔄 Session restaurée ! ${loadedMessages.length} messages chargés.`,
          timestamp: Date.now()
        });
      } else {
        // Nouveau chat, message complet
        welcomeMessages.push(
          {
            id: '1',
            role: 'system',
            content: `🧠 Session IA initialisée avec succès !`,
            timestamp: Date.now()
          },
          {
            id: '2',
            role: 'assistant',
            content: `Bonjour ! Je suis votre expert IA pour "${dataset.file_name}".\n\n` +
              `J'ai analysé votre dataset :\n` +
              `• ${summary.rows.toLocaleString()} lignes\n` +
              `• ${summary.columns} colonnes\n` +
              `• ${summary.patterns} patterns détectés\n` +
              `• ${summary.insights} insights découverts\n\n` +
              `Niveau de compréhension: ${session.understandingLevel || 'basic'}\n\n` +
              `Posez-moi n'importe quelle question sur vos données, je vais les analyser en temps réel !`,
            timestamp: Date.now()
          }
        );
      }

      // Combiner historique + messages de bienvenue
      setMessages([...loadedMessages, ...welcomeMessages]);

    } catch (error) {
      logger.error('[❌ DatasetChat] Failed to initialize session:', error);
      
      // Parser l'erreur pour afficher un message plus clair
      let errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      
      if (errorMessage.includes('DuckDB processing not completed')) {
        errorMessage = `⏳ Le dataset est encore en cours de traitement.\n\n` +
          `L'analyse DuckDB n'est pas encore terminée. Veuillez attendre quelques instants que le processing soit complété.\n\n` +
          `Statut actuel: ${dataset.status}\n\n` +
          `Rafraîchissez la page dans quelques secondes.`;
      }
      
      setError(errorMessage);
      
      setMessages([{
        id: '1',
        role: 'assistant',
        content: `❌ ${errorMessage}`,
        timestamp: Date.now()
      }]);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading || !expertSession) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    const userQuery = input;
    setInput('');
    setIsLoading(true);

    // ✅ SAVE LOADING STATE - Pour restaurer si l'utilisateur change de tab
    const loadingKey = `analysis_loading_${dataset.id}`;
    localStorage.setItem(loadingKey, JSON.stringify({
      timestamp: Date.now(),
      query: userQuery
    }));

    try {
      debugLog('[💬 DatasetChat] Sending query to expert agent:', userQuery);
      
      // Appeler le backend expert agent
      const result = await BigDataExpertService.query(expertSession.sessionId, userQuery, user?.id);

      debugLog('[✅ DatasetChat] Got result:', {
        confidence: result.confidence,
        hasSQL: !!result.sqlQuery,
        resultCount: result.resultCount
      });

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: result.answer,
        timestamp: Date.now(),
        query: result.sqlQuery,
        resultCount: result.resultCount,
        confidence: result.confidence,
        executionTime: result.execution_time_ms,
        insights: result.insights || [],
        exportFile: result.exportFile || undefined
      };

      setMessages(prev => [...prev, aiMessage]);

      // ✅ CLEAR LOADING STATE - Analysis terminée
      localStorage.removeItem(loadingKey);

      // ✅ Sauvegarder la conversation dans big_data_queries pour persistance
      if (user && expertSession.supabaseSessionId) {
        try {
          await supabase.from('big_data_queries').insert({
            session_id: expertSession.supabaseSessionId,
            user_id: user.id,
            dataset_id: dataset.id, // ✅ Ajouté
            question: userQuery,
            answer: result.answer,
            sql_query: result.sqlQuery || null,
            result_count: result.resultCount || 0,
            execution_time_ms: result.execution_time_ms || 0,
            confidence: result.confidence || null,
            insights: result.insights || []
          });
          debugLog('[✅ DatasetChat] Chat message saved to big_data_queries');
        } catch (saveError) {
          debugLog('[❌ DatasetChat] Failed to save chat message:', saveError);
        }
      }

      // ✅ Sauvegarder les insights dans Supabase
      if (result.insights && Array.isArray(result.insights) && result.insights.length > 0 && user) {
        debugLog('[💡 DatasetChat] Saving insights to Supabase:', result.insights.length);
        
        try {
          await BigDataInsightsService.createInsightsBatch(
            result.insights.map((insight: any) => {
              // ✅ FIX: Mapper les types de l'IA aux types InsightType (TypeScript)
              const typeMapping: { [key: string]: 'pattern' | 'anomaly' | 'correlation' | 'trend' | 'statistical' | 'business' | 'quality' } = {
                'pattern': 'pattern',
                'anomaly': 'anomaly',
                'correlation': 'correlation',
                'summary': 'statistical',
                'recommendation': 'business',
                'trend': 'trend'
              };
              
              return {
                user_id: user.id,
                dataset_id: dataset.id,
                session_id: expertSession.supabaseSessionId, // ✅ UUID réel de big_data_sessions
                type: typeMapping[insight.type] || 'pattern',
                category: insight.category || 'medium_value',
                confidence: insight.confidence || result.confidence || 0.7,
                title: insight.title || `Découverte via: "${userQuery}"`,
                description: insight.description || insight.findings || insight.message || 'Insight découvert par l\'IA',
                sql_query: result.sqlQuery,
                sql_result_count: result.resultCount,
                sql_execution_time_ms: result.execution_time_ms,
                discovered_by: 'ai_chat' as const,
                discovered_at: new Date().toISOString(),
                details: {
                  expert_session_string_id: expertSession.sessionId, // String ID pour debug/référence
                  query: userQuery,
                  original_type: insight.type // Garder le type original pour référence
                }
              };
            })
          );
          
          debugLog('[✅ DatasetChat] Insights saved to Supabase');
          
          // Notification visuelle
          const insightCount = result.insights?.length || 0;
          setMessages(prev => [...prev, {
            id: (Date.now() + 2).toString(),
            role: 'system',
            content: `✅ ${insightCount} insight${insightCount > 1 ? 's' : ''} sauvegardé${insightCount > 1 ? 's' : ''} dans vos Découvertes`,
            timestamp: Date.now()
          }]);
        } catch (insightError) {
          debugLog('[❌ DatasetChat] Failed to save insights:', insightError);
        }
      }

    } catch (error) {
      logger.error('[❌ DatasetChat] Query error:', error);
      
      // ✅ CLEAR LOADING STATE - Analysis échouée
      const loadingKey = `analysis_loading_${dataset.id}`;
      localStorage.removeItem(loadingKey);
      
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `❌ Désolé, une erreur s'est produite lors de l'analyse.\n\nErreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}\n\nPouvez-vous reformuler votre question ?`,
        timestamp: Date.now()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedQuestions = [
    "Quel est le livre le plus fréquent dans le dataset ?",
    "Quelle est la distribution des ratings ?",
    "Y a-t-il des anomalies ou valeurs aberrantes ?",
    "Montre-moi les statistiques principales",
    "Quelles sont les tendances principales ?",
    "Combien de livres uniques dans les données ?"
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-300px)]">
      {/* 🎨 Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 backdrop-blur-sm border-b border-purple-200 dark:border-slate-700 px-6 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold bg-gradient-to-r from-purple-700 to-indigo-700 dark:from-white dark:to-white bg-clip-text text-transparent">Big Data Expert</h2>
              <p className="text-xs text-purple-600 dark:text-gray-400">{dataset.file_name}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Removed Live indicator */}
          </div>
        </div>
      </div>
      
      {/* Erreur d'initialisation */}
      {error && (
        <div className="mx-6 mt-6 p-4 bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-400/40 rounded-xl">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-400 font-medium">Erreur d'initialisation</p>
              <p className="text-xs text-red-600 dark:text-red-300 mt-1 whitespace-pre-wrap">{error}</p>
            </div>
            <button
              onClick={initializeExpertSession}
              className="px-3 py-1 bg-red-100 hover:bg-red-200 dark:bg-red-500/30 dark:hover:bg-red-500/40 text-red-700 dark:text-red-400 rounded-lg text-xs transition-colors inline-flex items-center space-x-1"
            >
              <RefreshCw className="w-3 h-3" />
              <span>Réessayer</span>
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 p-6">
        {isInitializing ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-600 dark:text-purple-400" />
              <p className="text-purple-900 dark:text-white font-medium">Initialisation de l'IA...</p>
              <p className="text-sm text-purple-600 dark:text-gray-400 mt-2">Claude apprend votre dataset</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start space-x-3 ${
              message.role === 'user' ? 'justify-end' : ''
            }`}
          >
            {message.role === 'assistant' && (
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20">
                <Brain className="w-5 h-5 text-white" />
              </div>
            )}
            
            <div className={`max-w-2xl ${
              message.role === 'user'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-500 dark:to-indigo-500 text-white shadow-lg shadow-purple-500/20'
                : message.role === 'system'
                ? 'bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-500/30 dark:border-blue-400/40'
                : 'bg-gradient-to-br from-purple-50/90 via-purple-50/90 to-blue-50/90 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800 border border-purple-200 dark:border-slate-700 text-purple-900 dark:text-white shadow-sm'
            } rounded-2xl p-4`}>
              {message.role === 'assistant' ? (
                <div className="prose dark:prose-invert prose-sm max-w-none
                  prose-headings:text-purple-600 dark:prose-headings:text-purple-400 prose-headings:font-bold prose-headings:mb-3
                  prose-table:border-collapse prose-table:w-full prose-table:my-4 prose-table:border prose-table:border-purple-200 dark:prose-table:border-purple-500/30 prose-table:rounded-lg prose-table:overflow-hidden
                  prose-thead:bg-purple-100 dark:prose-thead:bg-purple-500/20 prose-thead:border-b prose-thead:border-purple-300 dark:prose-thead:border-purple-500/40
                  prose-th:border prose-th:border-purple-200 dark:prose-th:border-purple-500/30 prose-th:bg-purple-50 dark:prose-th:bg-purple-500/10 prose-th:p-3 prose-th:text-left prose-th:text-purple-900 dark:prose-th:text-purple-300 prose-th:font-semibold
                  prose-tbody:divide-y prose-tbody:divide-purple-200 dark:prose-tbody:divide-purple-500/20 prose-tbody:bg-gradient-to-br prose-tbody:from-purple-50 prose-tbody:to-blue-50 dark:prose-tbody:from-slate-800 dark:prose-tbody:to-slate-800
                  prose-tr:border-b prose-tr:border-purple-200 dark:prose-tr:border-purple-500/20 prose-tr:transition-colors prose-tr:hover:bg-purple-50 dark:prose-tr:hover:bg-purple-500/5
                  prose-td:border prose-td:border-purple-200 dark:prose-td:border-purple-500/20 prose-td:p-3 prose-td:text-purple-900 dark:prose-td:text-white
                  prose-strong:text-purple-700 dark:prose-strong:text-purple-300 prose-strong:font-semibold
                  prose-p:text-purple-900 dark:prose-p:text-white prose-p:leading-relaxed prose-p:mb-3
                  prose-ul:text-purple-900 dark:prose-ul:text-white prose-ul:my-2 prose-ul:pl-4
                  prose-ol:text-purple-900 dark:prose-ol:text-white prose-ol:my-2 prose-ol:pl-4
                  prose-li:text-purple-900 dark:prose-li:text-white prose-li:my-1
                  prose-code:text-purple-700 dark:prose-code:text-purple-300 prose-code:bg-purple-100 dark:prose-code:bg-purple-500/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                  prose-pre:bg-gradient-to-br prose-pre:from-purple-50 prose-pre:to-blue-50 dark:prose-pre:from-slate-900 dark:prose-pre:to-slate-900 prose-pre:border prose-pre:border-purple-200 dark:prose-pre:border-purple-500/30 prose-pre:p-4 prose-pre:rounded-lg
                  prose-a:text-purple-600 dark:prose-a:text-purple-400 prose-a:underline prose-a:hover:text-purple-700 dark:prose-a:hover:text-purple-300
                ">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
              
              {/* Confidence Badge */}
              {message.confidence !== undefined && (
                <div className="mt-2 inline-flex items-center space-x-2 px-3 py-1 bg-green-500/10 dark:bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30 dark:border-green-400/40 rounded-full text-xs">
                  <Sparkles className="w-3 h-3" />
                  <span>Confiance: {(message.confidence * 100).toFixed(0)}%</span>
                </div>
              )}
              
              {/* ✅ EXPORT BUTTONS - Style discret comme FluidChat */}
              {message.role === 'assistant' && message.resultCount && message.resultCount > 10 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-purple-600 dark:text-gray-500">
                  <span className="opacity-70">{message.resultCount.toLocaleString()} lignes</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleExport(message, 'csv')}
                      className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-md shadow-sm transition-all duration-200 hover:scale-105"
                      title="Export CSV"
                    >
                      <FileDown className="w-3.5 h-3.5 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors" />
                    </button>
                    
                    <button
                      onClick={() => handleExport(message, 'json')}
                      className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-md shadow-sm transition-all duration-200 hover:scale-105"
                      title="Export JSON"
                    >
                      <FileJson className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors" />
                    </button>
                    
                    <button
                      onClick={() => handleExport(message, 'markdown')}
                      className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-md shadow-sm transition-all duration-200 hover:scale-105"
                      title="Export Markdown"
                    >
                      <FileText className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors" />
                    </button>
                    
                    <button
                      onClick={() => handleExport(message, 'pdf')}
                      className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-md shadow-sm transition-all duration-200 hover:scale-105"
                      title="Export PDF"
                    >
                      <FileDown className="w-3.5 h-3.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* SQL Query */}
              {message.query && (
                <div className="mt-3 p-3 bg-purple-50 dark:bg-slate-800/80 border border-purple-200 dark:border-slate-700 rounded-lg">
                  <div className="flex items-center space-x-2 text-xs text-purple-700 dark:text-gray-400 mb-2">
                    <Database className="w-3 h-3" />
                    <span>Requête SQL</span>
                    {message.executionTime && (
                      <span className="text-green-600 dark:text-green-400">• {message.executionTime}ms</span>
                    )}
                  </div>
                  <code className="text-xs font-mono text-green-700 dark:text-green-400 block overflow-x-auto">
                    {message.query}
                  </code>
                  {message.resultCount !== undefined && (
                    <p className="text-xs text-purple-700 dark:text-gray-400 mt-2">
                      {message.resultCount.toLocaleString()} résultats
                    </p>
                  )}
                </div>
              )}
              
              {/* Insights découverts */}
              {message.insights && message.insights.length > 0 && (
                <div className="mt-3 p-3 bg-purple-100 dark:bg-purple-500/10 border border-purple-300 dark:border-purple-500/30 rounded-lg">
                  <div className="flex items-center space-x-2 text-xs text-purple-700 dark:text-purple-400 mb-2">
                    <Sparkles className="w-3 h-3" />
                    <span>{message.insights.length} insight{message.insights.length > 1 ? 's' : ''} découvert{message.insights.length > 1 ? 's' : ''}</span>
                  </div>
                  {message.insights.slice(0, 2).map((insight: any, idx: number) => (
                    <div key={idx} className="text-xs text-purple-700 dark:text-gray-400 mt-1">
                      • {insight.title}
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-purple-700 dark:text-gray-400 mt-2">
                {new Date(message.timestamp).toLocaleTimeString()}
              </p>
            </div>

            {message.role === 'user' && (
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border border-purple-200 dark:border-slate-700 rounded-2xl p-4">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 text-purple-600 dark:text-purple-400 animate-spin" />
                <span className="text-sm text-purple-700 dark:text-gray-400">Analyse en cours...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Suggested Questions */}
        {!isInitializing && messages.length <= 2 && !error && (
          <div className="px-6 pb-4">
            <p className="text-sm text-purple-700 dark:text-gray-400 mb-3 font-medium">Questions suggérées:</p>
            <div className="grid grid-cols-2 gap-2">
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => setInput(q)}
                  className="text-left p-3 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-lg text-sm text-purple-800 dark:text-white transition-all shadow-sm hover:shadow-md"
                >
                  {q}
                </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-purple-200 dark:border-slate-700 bg-gradient-to-r from-purple-50/50 to-blue-50/50 dark:from-slate-800/50 dark:to-slate-800/50 p-6">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={expertSession ? "Posez une question sur vos données..." : "Initialisation en cours..."}
            disabled={isLoading || !expertSession}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border-2 border-purple-200 dark:border-slate-700 rounded-xl text-purple-900 dark:text-white placeholder-purple-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:opacity-50 transition-all"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading || !expertSession}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/50 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 inline-flex items-center space-x-2"
          >
            <Send className="w-5 h-5" />
            <span>Envoyer</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatasetChat;



